// components/pages/ChartingV2/CompareChartsModal.tsx

import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { IPerioChart } from '@dataTypes/chartingFeature.types'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { setCompareCharts } from '@store/reducers/charting.reducer'
import {
  ComboBoxComponent,
  ChangeEventArgs,
} from '@syncfusion/ej2-react-dropdowns'

type ChartOption = {
  id: string
  name: string
  htmlAttributes: Record<string, string>
}

interface CompareChartsModalProps {
  show: boolean
  onHide: () => void
}

const CompareChartsModal: React.FC<CompareChartsModalProps> = ({
  show,
  onHide,
}) => {
  const dispatch = useAppDispatch()
  const chartHistory: IPerioChart[] = useAppSelector(
    (state) => state.charting.perioCharts,
  )
  const [chartA, setChartA] = useState<IPerioChart | null>(null)
  const [chartB, setChartB] = useState<IPerioChart | null>(null)

  const fields = {
    text: 'name',
    value: 'id',
    htmlAttributes: 'htmlAttributes',
  }

  const chartOptions: ChartOption[] = useMemo(() => {
    return chartHistory
      .slice()
      .sort(
        (a, b) =>
          new Date(b.date_time_created_at ?? 0).getTime() -
          new Date(a.date_time_created_at ?? 0).getTime(),
      )
      .map((chart) => ({
        id: chart.chart_id,
        name: `${new Date(chart.date_time_created_at ?? '').toLocaleDateString(
          undefined,
          {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          },
        )} (${chart.title}) - ${formatProviderDisplayName(chart.provider, 'No provider')}`,
        htmlAttributes: { disabled: 'false' },
      }))
  }, [chartHistory])

  const chartOptionsForA: ChartOption[] = useMemo(() => {
    const otherSelectedId = chartB?.chart_id ?? ''
    return chartOptions.map((o) =>
      o.id === otherSelectedId
        ? { ...o, htmlAttributes: { disabled: 'true' } }
        : { ...o, htmlAttributes: { disabled: 'false' } },
    )
  }, [chartOptions, chartB?.chart_id])

  const chartOptionsForB: ChartOption[] = useMemo(() => {
    const otherSelectedId = chartA?.chart_id ?? ''
    return chartOptions.map((o) =>
      o.id === otherSelectedId
        ? { ...o, htmlAttributes: { disabled: 'true' } }
        : { ...o, htmlAttributes: { disabled: 'false' } },
    )
  }, [chartOptions, chartA?.chart_id])

  useEffect(() => {
    if (!show) onHide()
  }, [show, onHide])

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Compare charts</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-2">
          <Col className="text-gray-700">
            Select two charts to view differences side-by-side.
          </Col>
        </Row>

        <Row className="gx-2">
          <Col>
            <label className="mb-1">Select first chart</label>
            <ComboBoxComponent
              dataSource={chartOptionsForA}
              allowCustom={false}
              allowFiltering={true}
              filterType="Contains"
              fields={fields}
              placeholder="Select a chart..."
              value={chartA?.chart_id ?? ''}
              change={(e: ChangeEventArgs) => {
                const selectedId = String(e.value ?? '')
                if (selectedId && selectedId === (chartB?.chart_id ?? '')) {
                  return
                }
                const selectedChart = chartHistory.find(
                  (c) => c.chart_id === selectedId,
                )
                setChartA(selectedChart ?? null)
              }}
              cssClass="pe-0"
            />
          </Col>

          <Col>
            <label className="mb-1">Select second chart</label>
            <ComboBoxComponent
              dataSource={chartOptionsForB}
              allowCustom={false}
              allowFiltering={true}
              filterType="Contains"
              fields={fields}
              placeholder="Select a chart..."
              value={chartB?.chart_id ?? ''}
              change={(e: ChangeEventArgs) => {
                const selectedId = String(e.value ?? '')
                if (selectedId && selectedId === (chartA?.chart_id ?? '')) {
                  return
                }
                const selectedChart = chartHistory.find(
                  (c) => c.chart_id === selectedId,
                )
                setChartB(selectedChart ?? null)
              }}
              cssClass="pe-0"
            />
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onHide} className="button-secondary me-1">
          Cancel
        </Button>
        <Button
          onClick={() => {
            onHide()
            if (!(!chartA || !chartB || chartA === chartB)) {
              dispatch(setCompareCharts({ chartA, chartB }))
              setChartA(null)
              setChartB(null)
            }
          }}
          className="button-primary"
          disabled={!chartA || !chartB || chartA === chartB}
        >
          Compare
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CompareChartsModal
